import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { YeehroCalendlyBooking } from '@app/models';
import { YeehroCalendlyBookingService } from './yeehro-calendly-booking.service';
import { MeetingReminderDialogComponent } from '../modules/notifications/meeting-reminder-dialog/meeting-reminder-dialog.component';

const REMINDER_LEAD_MS = 10 * 60 * 1000;      // pop-up shows 10 minutes before start
const CHECK_INTERVAL_MS = 60 * 1000;          // local check, no server call
const FALLBACK_REFRESH_MS = 30 * 60 * 1000;   // reload if nothing (socket included) has refreshed the list in this long
const FETCH_WINDOW_MS = 12 * 60 * 60 * 1000;  // load meetings for the next 12 hours

// In-app meeting reminder (case 1504). Holds the logged-in host's upcoming meetings and pops a
// dialog when a meeting crosses its 10-minute mark. The main portal starts it and feeds it socket refreshes.
@Injectable({ providedIn: 'root' })
export class MeetingReminderService {
  private hostId: string = null;
  private meetings: YeehroCalendlyBooking[] = [];
  private lastRefreshedAt: number = 0;
  private lastCheckedAt: number = 0;
  private checkTimer: any = null;

  private reminderDialogRef: MatDialogRef<MeetingReminderDialogComponent> = null;
  private reminderMeetings$: BehaviorSubject<YeehroCalendlyBooking[]> = new BehaviorSubject<YeehroCalendlyBooking[]>([]);

  constructor(
    private _bookingService: YeehroCalendlyBookingService,
    private _dialog: MatDialog
  ) {}


  // Loads the host's meetings and starts the once-a-minute check.
  async initMeetingReminders(hostId: string) {
    if (!hostId) return;
    this.clearMeetingReminders();
    this.hostId = hostId;

    await this.getUpcomingMeetings();
    this.checkForUpcomingMeetings();
    this.checkTimer = setInterval(() => this.checkForUpcomingMeetings(), CHECK_INTERVAL_MS);
  }


  // Stops the check and closes any open reminder (portal teardown / logout).
  clearMeetingReminders() {
    if (this.checkTimer) clearInterval(this.checkTimer);
    this.checkTimer = null;
    this.hostId = null;
    this.meetings = [];
    this.lastRefreshedAt = 0;
    this.lastCheckedAt = 0;
    this.reminderDialogRef?.close();
  }


  // Reloads meetings that haven't ended yet. Called on load, on socket booking changes, on reconnect, and by the fallback.
  async getUpcomingMeetings() {
    if (!this.hostId) return;

    const previousStartById = new Map(this.meetings.map(m => [String(m?._id), this.toTime(m?.start)]));
    const now = new Date();

    try {
      const meetings = await lastValueFrom(this._bookingService.getHostBookingsInRange(this.hostId, now, new Date(now.getTime() + FETCH_WINDOW_MS)));
      this.meetings = (meetings || []).sort((a, b) => this.toTime(a?.start) - this.toTime(b?.start));
      this.lastRefreshedAt = Date.now();
    } catch (err) {
      console.log('Unable to load upcoming meetings for reminders: ', err);
      return;
    }

    this.syncReminderDialog();

    // A meeting booked or moved to start inside the 10-minute window never crosses its mark, so show it now.
    // Skipped before the first check, since that check already picks up anything inside the window.
    if (!this.lastCheckedAt) return;
    const nowMs = Date.now();
    const movedIntoWindow = this.meetings.filter(m => {
      const start = this.toTime(m?.start);
      const isNewOrMoved = previousStartById.get(String(m?._id)) !== start;
      return isNewOrMoved && start - REMINDER_LEAD_MS <= nowMs && start > nowMs;
    });
    this.showReminders(movedIntoWindow);
  }


  // Runs once a minute against the loaded list. A meeting shows when its 10-minute mark falls
  // between the last check and now, so each one shows once without tracking ids.
  checkForUpcomingMeetings() {
    const now = Date.now();
    const since = this.lastCheckedAt;
    this.lastCheckedAt = now;

    if (now - this.lastRefreshedAt > FALLBACK_REFRESH_MS) this.getUpcomingMeetings();

    this.meetings = this.meetings.filter(m => this.toTime(m?.end) > now); // ended meetings are done
    this.syncReminderDialog();

    const due = this.meetings.filter(m => {
      const start = this.toTime(m?.start);
      const mark = start - REMINDER_LEAD_MS;
      return mark > since && mark <= now && start > now; // skip anything already started (e.g. laptop was asleep)
    });
    this.showReminders(due);
  }


  // Adds meetings to the open reminder, or opens one. Never stacks a second dialog.
  private showReminders(meetings: YeehroCalendlyBooking[]) {
    if (!meetings?.length) return;

    const shown = this.reminderMeetings$.getValue();
    const added = meetings.filter(m => !shown.some(s => String(s?._id) === String(m?._id)));
    if (!added.length) return;

    this.reminderMeetings$.next([...shown, ...added].sort((a, b) => this.toTime(a?.start) - this.toTime(b?.start)));
    this.playReminderSound();

    if (this.reminderDialogRef) return;
    this.reminderDialogRef = this._dialog.open(MeetingReminderDialogComponent, {
      panelClass: 'yc-dialog-panel',
      disableClose: true, // must press a button so the reminder gets read
      autoFocus: false,
      data: { meetings$: this.reminderMeetings$ }
    });
    this.reminderDialogRef.afterClosed().subscribe(() => {
      this.reminderDialogRef = null;
      this.reminderMeetings$.next([]);
    });
  }


  // Keeps open reminder rows in step with the latest list: fresh data in, cancelled / reassigned /
  // moved-later / ended meetings out. Closes the dialog when nothing is left.
  private syncReminderDialog() {
    if (!this.reminderDialogRef) return;

    const now = Date.now();
    const rows = this.reminderMeetings$.getValue()
      .map(row => this.meetings.find(m => String(m?._id) === String(row?._id)))
      .filter(m => m && this.toTime(m?.start) - REMINDER_LEAD_MS <= now && this.toTime(m?.end) > now);

    if (!rows.length) {
      this.reminderDialogRef.close();
      return;
    }
    this.reminderMeetings$.next(rows);
  }


  private playReminderSound() {
    const audio = new Audio('/uploads/system_images/sounds/message_notification_long.wav');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  }


  private toTime(value: any): number {
    const time = new Date(value).getTime();
    return isNaN(time) ? 0 : time;
  }
}
